// Project Management Page for PFMEA
// Links to Magnus-Core projects with RBAC context
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Folder,
    FolderOpen,
    Search,
    RefreshCw,
    ArrowUpDown,
    CheckCircle,
    Clock,
    Archive,
    Play,
    FileText,
    AlertTriangle,
    Building2,
    Calendar,
    Tag,
    Users,
    Link2,
    Filter,
    ChevronDown,
    Sparkles,
    Plus,
    X,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import { fetchProjects, ProjectMetadata, savePfmeaToProject } from '../services/projectService';
import clsx from 'clsx';

// Stage color mapping
const STAGE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
    concept: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-300' },
    design: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-300' },
    prototype: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-300' },
    production: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-300' },
    maintenance: { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-700 dark:text-slate-300', border: 'border-slate-300' },
};

// Standard color mapping
const STANDARD_COLORS: Record<string, { label: string; color: string }> = {
    'AIAG_VDA': { label: 'AIAG-VDA', color: 'text-blue-600 dark:text-blue-400' },
    'AIAG_V4': { label: 'AIAG V4', color: 'text-purple-600 dark:text-purple-400' },
    'ISO_14971': { label: 'ISO 14971', color: 'text-red-600 dark:text-red-400' },
    'ISO_26262': { label: 'ISO 26262', color: 'text-amber-600 dark:text-amber-400' },
    'MIL_STD_1629A': { label: 'MIL-STD', color: 'text-green-600 dark:text-green-400' },
    'IEC_61508': { label: 'IEC 61508', color: 'text-cyan-600 dark:text-cyan-400' },
    'CUSTOM': { label: 'Custom', color: 'text-slate-600 dark:text-slate-400' },
};

// New Project Form interface
interface NewProjectForm {
    name: string;
    displayName: string;
    customer: string;
    stage: 'concept' | 'design' | 'prototype' | 'production' | 'maintenance';
    fmeaStandard: string;
}

const ProjectManagementPage: React.FC = () => {
    const { theme } = useTheme();
    const { user, hasPermission } = useAuth();
    const { setActiveProject, setActiveProjectName, loadProjectData } = useAppContext();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    // State
    const [projects, setProjects] = useState<ProjectMetadata[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [stageFilter, setStageFilter] = useState<string>('all');
    const [sortField, setSortField] = useState<'name' | 'modified' | 'stage'>('modified');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [loadingProject, setLoadingProject] = useState<string | null>(null);
    const [showStageFilter, setShowStageFilter] = useState(false);

    // New Project Modal State
    const [showNewProjectModal, setShowNewProjectModal] = useState(false);
    const [creatingProject, setCreatingProject] = useState(false);
    const [newProjectForm, setNewProjectForm] = useState<NewProjectForm>({
        name: '',
        displayName: '',
        customer: '',
        stage: 'design',
        fmeaStandard: 'AIAG_VDA',
    });

    // Load projects on mount
    useEffect(() => {
        loadProjects();
    }, []);

    const loadProjects = async () => {
        setLoading(true);
        setError(null);
        try {
            const projectList = await fetchProjects();
            setProjects(projectList);
        } catch (err: any) {
            setError(err.message || 'Failed to load projects');
        } finally {
            setLoading(false);
        }
    };

    // Filtered and sorted projects
    const filteredProjects = useMemo(() => {
        return projects
            .filter(p => {
                // Search filter
                if (searchQuery) {
                    const query = searchQuery.toLowerCase();
                    return (
                        p.name.toLowerCase().includes(query) ||
                        p.displayName.toLowerCase().includes(query) ||
                        p.customer.toLowerCase().includes(query)
                    );
                }
                return true;
            })
            .filter(p => {
                // Stage filter
                if (stageFilter !== 'all') {
                    return p.stage === stageFilter;
                }
                return true;
            })
            .sort((a, b) => {
                let comparison = 0;
                switch (sortField) {
                    case 'name':
                        comparison = a.displayName.localeCompare(b.displayName);
                        break;
                    case 'modified':
                        comparison = new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
                        break;
                    case 'stage':
                        comparison = a.stage.localeCompare(b.stage);
                        break;
                }
                return sortDirection === 'asc' ? comparison : -comparison;
            });
    }, [projects, searchQuery, stageFilter, sortField, sortDirection]);

    // Handle project selection - loads project data into context
    const handleSelectProject = async (project: ProjectMetadata) => {
        if (!hasPermission('pfmea.view')) {
            alert('You do not have permission to view PFMEA data.');
            return;
        }

        setLoadingProject(project.id);
        try {
            // Load project data into context (this updates pfmeaItems, processFlow, controlPlans)
            const success = await loadProjectData(project.name);

            if (success) {
                // Set display name for UI
                setActiveProject(project.displayName);
                // Set internal name for API calls (e.g., "Aerator_Pro")
                setActiveProjectName(project.name);
                // Navigate to dashboard with loaded project
                navigate('/');
                console.log(`[ProjectManagement] Loaded project: ${project.displayName}`);
            } else {
                console.warn(`[ProjectManagement] Failed to load project: ${project.name}`);
                alert(`Failed to load project "${project.displayName}". Please try again.`);
            }
        } catch (err) {
            console.error('Error loading project:', err);
            alert('An error occurred while loading the project.');
        } finally {
            setLoadingProject(null);
        }
    };

    // Toggle sort
    const toggleSort = (field: 'name' | 'modified' | 'stage') => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    // Get status icon
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active':
                return <CheckCircle size={14} className="text-green-500" />;
            case 'inactive':
                return <Clock size={14} className="text-amber-500" />;
            case 'archived':
                return <Archive size={14} className="text-slate-400" />;
            default:
                return null;
        }
    };

    // Handle creating a new project
    const handleCreateProject = async () => {
        if (!newProjectForm.name.trim()) {
            alert('Please enter a project name');
            return;
        }

        if (!hasPermission('pfmea.edit')) {
            alert('You do not have permission to create projects.');
            return;
        }

        setCreatingProject(true);
        try {
            const projectName = newProjectForm.name.trim().replace(/\s+/g, '_');
            const success = await savePfmeaToProject(
                projectName,
                { pfmeaItems: [], processFlow: null, controlPlan: null },
                {
                    id: projectName,
                    displayName: newProjectForm.displayName || newProjectForm.name,
                    customer: newProjectForm.customer || 'Unknown',
                    status: 'active',
                    stage: newProjectForm.stage,
                    fmeaStandard: newProjectForm.fmeaStandard,
                }
            );

            if (success) {
                console.log(`[ProjectManagement] Created project: ${projectName}`);
                // Reset form and close modal
                setNewProjectForm({
                    name: '',
                    displayName: '',
                    customer: '',
                    stage: 'design',
                    fmeaStandard: 'AIAG_VDA',
                });
                setShowNewProjectModal(false);
                // Reload projects to show the new one
                await loadProjects();
                // Optionally auto-select the new project
                setActiveProject(newProjectForm.displayName || newProjectForm.name);
                setActiveProjectName(projectName);
                navigate('/');
            } else {
                alert('Failed to create project. Please try again.');
            }
        } catch (err) {
            console.error('Error creating project:', err);
            alert('An error occurred while creating the project.');
        } finally {
            setCreatingProject(false);
        }
    };

    return (
        <div className={clsx('h-full flex flex-col', isDark ? 'bg-slate-900' : 'bg-slate-50')}>
            {/* Header */}
            <header className={clsx(
                'h-16 border-b flex items-center justify-between px-6 shrink-0',
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            )}>
                <div className="flex items-center gap-4">
                    <FolderOpen size={28} className="text-primary-500" />
                    <div>
                        <h1 className={clsx('text-xl font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                            Project Management
                        </h1>
                        <p className="text-xs text-slate-500">
                            Select a project to load PFMEA data • Connected to Magnus-Core
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* User Info */}
                    {user && (
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-700">
                            <Users size={16} className="text-slate-500" />
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                                {user.displayName}
                            </span>
                            <span className={clsx(
                                'text-xs px-2 py-0.5 rounded-full',
                                user.role === 'platform_admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300' :
                                    user.role === 'org_admin' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' :
                                        user.role === 'engineer' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' :
                                            'bg-slate-100 text-slate-600 dark:bg-slate-600 dark:text-slate-300'
                            )}>
                                {user.role}
                            </span>
                        </div>
                    )}

                    {/* New Project Button */}
                    <button
                        onClick={() => setShowNewProjectModal(true)}
                        className={clsx(
                            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            'bg-primary-500 hover:bg-primary-600 text-white'
                        )}
                    >
                        <Plus size={16} />
                        New Project
                    </button>

                    {/* Refresh Button */}
                    <button
                        onClick={loadProjects}
                        disabled={loading}
                        className={clsx(
                            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700',
                            loading && 'opacity-50 cursor-not-allowed'
                        )}
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>
            </header>

            {/* Toolbar */}
            <div className={clsx(
                'flex items-center gap-4 px-6 py-4 border-b',
                isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'
            )}>
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search projects..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={clsx(
                            'w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none',
                            isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500'
                        )}
                    />
                </div>

                {/* Stage Filter */}
                <div className="relative">
                    <button
                        onClick={() => setShowStageFilter(!showStageFilter)}
                        className={clsx(
                            'flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors',
                            isDark ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                        )}
                    >
                        <Filter size={16} />
                        {stageFilter === 'all' ? 'All Stages' : stageFilter.charAt(0).toUpperCase() + stageFilter.slice(1)}
                        <ChevronDown size={14} className={clsx('transition-transform', showStageFilter && 'rotate-180')} />
                    </button>

                    {showStageFilter && (
                        <div className={clsx(
                            'absolute top-full left-0 mt-1 w-40 rounded-lg shadow-xl border py-1 z-50',
                            isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                        )}>
                            {['all', 'concept', 'design', 'prototype', 'production', 'maintenance'].map(stage => (
                                <button
                                    key={stage}
                                    onClick={() => { setStageFilter(stage); setShowStageFilter(false); }}
                                    className={clsx(
                                        'w-full text-left px-4 py-2 text-sm transition-colors',
                                        stageFilter === stage ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' :
                                            isDark ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50'
                                    )}
                                >
                                    {stage === 'all' ? 'All Stages' : stage.charAt(0).toUpperCase() + stage.slice(1)}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Sort */}
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <span>Sort by:</span>
                    {(['name', 'modified', 'stage'] as const).map(field => (
                        <button
                            key={field}
                            onClick={() => toggleSort(field)}
                            className={clsx(
                                'px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1',
                                sortField === field
                                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                    : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                            )}
                        >
                            {field.charAt(0).toUpperCase() + field.slice(1)}
                            {sortField === field && (
                                <ArrowUpDown size={12} className={sortDirection === 'asc' ? 'rotate-180' : ''} />
                            )}
                        </button>
                    ))}
                </div>

                {/* Project Count */}
                <div className="ml-auto text-sm text-slate-500">
                    {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <RefreshCw size={32} className="animate-spin mx-auto mb-4 text-primary-500" />
                            <p className="text-slate-500">Loading projects from Magnus-Core...</p>
                        </div>
                    </div>
                ) : error ? (
                    <div className={clsx(
                        'flex items-center gap-4 p-6 rounded-xl border mx-auto max-w-md',
                        isDark ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
                    )}>
                        <AlertTriangle size={32} className="text-red-500" />
                        <div>
                            <h3 className="font-semibold text-red-700 dark:text-red-300">Error Loading Projects</h3>
                            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                            <button
                                onClick={loadProjects}
                                className="mt-2 text-sm text-primary-600 hover:underline"
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                ) : filteredProjects.length === 0 ? (
                    <div className="text-center py-12">
                        <Folder size={48} className="mx-auto mb-4 text-slate-300" />
                        <h3 className={clsx('text-lg font-semibold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                            No Projects Found
                        </h3>
                        <p className="text-slate-500 mb-4">
                            {searchQuery || stageFilter !== 'all'
                                ? 'Try adjusting your search or filters'
                                : 'No projects are available in Magnus-Core'}
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {filteredProjects.map(project => {
                            const stageStyle = STAGE_COLORS[project.stage] || STAGE_COLORS.design;
                            const standardInfo = STANDARD_COLORS[project.fmeaStandard] || STANDARD_COLORS.CUSTOM;
                            const isLoading = loadingProject === project.id;

                            return (
                                <button
                                    key={project.id}
                                    onClick={() => handleSelectProject(project)}
                                    disabled={isLoading}
                                    className={clsx(
                                        'text-left p-5 rounded-xl border transition-all hover:shadow-lg group',
                                        isDark
                                            ? 'bg-slate-800 border-slate-700 hover:border-primary-500/50'
                                            : 'bg-white border-slate-200 hover:border-primary-300',
                                        isLoading && 'opacity-50 cursor-wait'
                                    )}
                                >
                                    {/* Header */}
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <Folder size={20} className="text-primary-500 group-hover:text-primary-400 transition-colors" />
                                            <span className={clsx(
                                                'text-xs font-medium px-2 py-0.5 rounded-full',
                                                stageStyle.bg, stageStyle.text
                                            )}>
                                                {project.stage}
                                            </span>
                                        </div>
                                        {getStatusIcon(project.status)}
                                    </div>

                                    {/* Project Name */}
                                    <h3 className={clsx(
                                        'font-semibold text-base mb-1 line-clamp-2 group-hover:text-primary-500 transition-colors',
                                        isDark ? 'text-white' : 'text-slate-800'
                                    )}>
                                        {project.displayName}
                                    </h3>

                                    {/* Customer */}
                                    <div className="flex items-center gap-1 text-xs text-slate-500 mb-3">
                                        <Building2 size={12} />
                                        {project.customer}
                                    </div>

                                    {/* Standard */}
                                    <div className={clsx(
                                        'flex items-center gap-1 text-xs font-medium mb-3',
                                        standardInfo.color
                                    )}>
                                        <Tag size={12} />
                                        {standardInfo.label}
                                    </div>

                                    {/* Counts */}
                                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                                        <span className="flex items-center gap-1">
                                            <FileText size={12} />
                                            {project.itemCount} items
                                        </span>
                                    </div>

                                    {/* Footer */}
                                    <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700">
                                        <div className="flex items-center gap-1 text-xs text-slate-400">
                                            <Calendar size={12} />
                                            {new Date(project.lastModified).toLocaleDateString()}
                                        </div>
                                        <div className={clsx(
                                            'flex items-center gap-1 text-xs font-medium transition-colors',
                                            'text-primary-500 group-hover:text-primary-400'
                                        )}>
                                            {isLoading ? (
                                                <>
                                                    <RefreshCw size={12} className="animate-spin" />
                                                    Loading...
                                                </>
                                            ) : (
                                                <>
                                                    <Play size={12} />
                                                    Load Project
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Connection Status Footer */}
            <footer className={clsx(
                'h-10 border-t flex items-center justify-between px-6 text-xs',
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            )}>
                <div className="flex items-center gap-2 text-slate-500">
                    <Link2 size={14} className="text-green-500" />
                    <span>Connected to Magnus-Core Backend</span>
                </div>
                <div className="flex items-center gap-4 text-slate-500">
                    <span>Last sync: {new Date().toLocaleTimeString()}</span>
                    {user && (
                        <span className="flex items-center gap-1">
                            <Sparkles size={12} className="text-amber-500" />
                            RBAC: {user.permissions.length} permissions
                        </span>
                    )}
                </div>
            </footer>

            {/* New Project Modal */}
            {showNewProjectModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className={clsx(
                        'w-full max-w-md rounded-xl shadow-2xl p-6',
                        isDark ? 'bg-slate-800' : 'bg-white'
                    )}>
                        <div className="flex items-center justify-between mb-6">
                            <h2 className={clsx('text-xl font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                                Create New Project
                            </h2>
                            <button
                                onClick={() => setShowNewProjectModal(false)}
                                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                            >
                                <X size={20} className="text-slate-500" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Project Name */}
                            <div>
                                <label className={clsx('block text-sm font-medium mb-1', isDark ? 'text-slate-300' : 'text-slate-700')}>
                                    Project Name *
                                </label>
                                <input
                                    type="text"
                                    value={newProjectForm.name}
                                    onChange={(e) => setNewProjectForm(prev => ({ ...prev, name: e.target.value, displayName: e.target.value }))}
                                    placeholder="e.g., Automotive Brake Assembly"
                                    className={clsx(
                                        'w-full px-4 py-2.5 rounded-lg border text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none',
                                        isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'
                                    )}
                                />
                            </div>

                            {/* Customer */}
                            <div>
                                <label className={clsx('block text-sm font-medium mb-1', isDark ? 'text-slate-300' : 'text-slate-700')}>
                                    Customer / Organization
                                </label>
                                <input
                                    type="text"
                                    value={newProjectForm.customer}
                                    onChange={(e) => setNewProjectForm(prev => ({ ...prev, customer: e.target.value }))}
                                    placeholder="e.g., ACME Corp"
                                    className={clsx(
                                        'w-full px-4 py-2.5 rounded-lg border text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none',
                                        isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'
                                    )}
                                />
                            </div>

                            {/* Stage */}
                            <div>
                                <label className={clsx('block text-sm font-medium mb-1', isDark ? 'text-slate-300' : 'text-slate-700')}>
                                    Development Stage
                                </label>
                                <select
                                    value={newProjectForm.stage}
                                    onChange={(e) => setNewProjectForm(prev => ({ ...prev, stage: e.target.value as any }))}
                                    className={clsx(
                                        'w-full px-4 py-2.5 rounded-lg border text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none',
                                        isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'
                                    )}
                                >
                                    <option value="concept">Concept</option>
                                    <option value="design">Design</option>
                                    <option value="prototype">Prototype</option>
                                    <option value="production">Production</option>
                                    <option value="maintenance">Maintenance</option>
                                </select>
                            </div>

                            {/* FMEA Standard */}
                            <div>
                                <label className={clsx('block text-sm font-medium mb-1', isDark ? 'text-slate-300' : 'text-slate-700')}>
                                    PFMEA Standard
                                </label>
                                <select
                                    value={newProjectForm.fmeaStandard}
                                    onChange={(e) => setNewProjectForm(prev => ({ ...prev, fmeaStandard: e.target.value }))}
                                    className={clsx(
                                        'w-full px-4 py-2.5 rounded-lg border text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none',
                                        isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'
                                    )}
                                >
                                    <option value="AIAG_VDA">AIAG-VDA (Automotive)</option>
                                    <option value="AIAG_V4">AIAG V4</option>
                                    <option value="ISO_14971">ISO 14971 (Medical)</option>
                                    <option value="ISO_26262">ISO 26262 (Automotive Safety)</option>
                                    <option value="IEC_61508">IEC 61508 (Functional Safety)</option>
                                    <option value="MIL_STD_1629A">MIL-STD-1629A (Aerospace/Defense)</option>
                                    <option value="CUSTOM">Custom</option>
                                </select>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                            <button
                                onClick={() => setShowNewProjectModal(false)}
                                className={clsx(
                                    'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                                    isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                )}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateProject}
                                disabled={creatingProject || !newProjectForm.name.trim()}
                                className={clsx(
                                    'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                                    'bg-primary-500 hover:bg-primary-600 text-white',
                                    (creatingProject || !newProjectForm.name.trim()) && 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                {creatingProject ? (
                                    <>
                                        <RefreshCw size={16} className="animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Plus size={16} />
                                        Create Project
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectManagementPage;
